import { fetchPage, patchResource, type ListParams, type PageResponse } from './collections';
import { apiFetch } from './http';

/**
 * Knowledge base (`AM-132 point 11–12`, 07 §4.14): `GET/POST /knowledge-articles`, its detail with
 * `If-Match`, the fixed four-state graph via `POST .../transition`, and the ticket's own links.
 * Bodies are Markdown/plain text (D49): nothing here renders or sanitises them, exactly like a
 * ticket's comments.
 */

/** `ck_knowledge_articles_status` (00 §5.14, `09` §16): fixed in code, the tenant does not configure it. */
export const KNOWLEDGE_STATUSES = ['draft', 'in_review', 'published', 'archived'] as const;

export type KnowledgeStatus = (typeof KNOWLEDGE_STATUSES)[number];

/** `ck_knowledge_articles_visibility`, independent of the status. */
export const KNOWLEDGE_VISIBILITIES = ['internal', 'customer'] as const;

export type KnowledgeVisibility = (typeof KNOWLEDGE_VISIBILITIES)[number];

/** `ck_ticket_knowledge_links_link_type`; `created_from` is written only by the alta with `fromTicketId`. */
export const KNOWLEDGE_LINK_TYPES = ['used_in_resolution', 'created_from', 'suggested'] as const;

export type KnowledgeLinkType = (typeof KNOWLEDGE_LINK_TYPES)[number];

/** The `summary` shape (07 §1.9), what the collection returns. */
export interface KnowledgeArticleSummary {
  id: string;
  articleNumber: string;
  title: string;
  status: KnowledgeStatus;
  visibility: KnowledgeVisibility;
  version: number;
  categoryId?: string | null;
  categoryName?: string | null;
  serviceId?: string | null;
  serviceName?: string | null;
  authorUserId?: string | null;
  ownerUserId?: string | null;
  publishedAt?: string | null;
  reviewDueOn?: string | null;
  archivedAt?: string | null;
  viewCount: number;
  updatedAt: string;
  eTag: string;
}

/** The `detail` shape: the summary plus the body of the article. */
export interface KnowledgeArticleDetail extends KnowledgeArticleSummary {
  symptoms?: string | null;
  body?: string | null;
  resolution?: string | null;
  archiveReason?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
  updatedByUserId?: string | null;
}

export interface CreateKnowledgeArticleInput {
  title: string;
  categoryId?: string | null;
  serviceId?: string | null;
  symptoms?: string | null;
  body?: string | null;
  resolution?: string | null;
  visibility?: KnowledgeVisibility;
  ownerUserId?: string | null;
  fromTicketId?: string | null;
}

/** A merge patch (RFC 7396); `If-Match` travels apart, as the header `patchResource` sets. */
export type UpdateKnowledgeArticleInput = Record<string, unknown> & {
  title?: string;
  categoryId?: string | null;
  serviceId?: string | null;
  symptoms?: string | null;
  body?: string | null;
  resolution?: string | null;
  visibility?: KnowledgeVisibility;
  ownerUserId?: string | null;
};

export interface TransitionKnowledgeArticleInput {
  to: KnowledgeStatus;
  comment?: string | null;
}

/** One row of `GET /tickets/{id}/knowledge-links`. */
export interface TicketKnowledgeLink {
  knowledgeArticleId: string;
  articleNumber: string;
  title: string;
  status: KnowledgeStatus;
  visibility: KnowledgeVisibility;
  linkType: KnowledgeLinkType;
  createdAt: string;
  createdByUserId?: string | null;
}

export const knowledgeApi = {
  list: (
    params: ListParams,
    signal?: AbortSignal,
  ): Promise<PageResponse<KnowledgeArticleSummary>> =>
    fetchPage<KnowledgeArticleSummary>('/api/v1/knowledge-articles', params, signal),
  get: (id: string, signal?: AbortSignal): Promise<KnowledgeArticleDetail> =>
    apiFetch<KnowledgeArticleDetail>(
      `/api/v1/knowledge-articles/${id}`,
      signal === undefined ? {} : { signal },
    ),
  create: (body: CreateKnowledgeArticleInput): Promise<KnowledgeArticleDetail> =>
    apiFetch<KnowledgeArticleDetail>('/api/v1/knowledge-articles', { method: 'POST', body }),
  patch: (
    id: string,
    body: UpdateKnowledgeArticleInput,
    etag: string,
  ): Promise<KnowledgeArticleDetail> =>
    patchResource<KnowledgeArticleDetail>(`/api/v1/knowledge-articles/${id}`, body, etag),
  transition: (
    id: string,
    body: TransitionKnowledgeArticleInput,
  ): Promise<KnowledgeArticleDetail> =>
    apiFetch<KnowledgeArticleDetail>(`/api/v1/knowledge-articles/${id}/transition`, {
      method: 'POST',
      body,
    }),
  linksOf: async (ticketId: string, signal?: AbortSignal): Promise<TicketKnowledgeLink[]> =>
    (
      await apiFetch<{ items: TicketKnowledgeLink[] }>(
        `/api/v1/tickets/${ticketId}/knowledge-links`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  link: (
    ticketId: string,
    body: { knowledgeArticleId: string; linkType: KnowledgeLinkType },
  ): Promise<TicketKnowledgeLink> =>
    apiFetch<TicketKnowledgeLink>(`/api/v1/tickets/${ticketId}/knowledge-links`, {
      method: 'POST',
      body,
    }),
};
